import { useState, useEffect, useMemo } from 'react';
import { useGlotaTranslationContext } from '../contexts/GlotaTranslationContext';
import glotaTranslationService from '../services/glota-translation.service';

// Mapeamento de locales para o backend
const LOCALE_MAP = {
  'pt-BR': 'pt-br',
  'es-ES': 'es',
  'en-US': 'en-us',
};

/**
 * Hook para tradução assíncrona com estado
 * Retorna o texto traduzido e atualiza automaticamente quando o locale muda
 * Agora usa tradução síncrona local primeiro para evitar flash
 */
export const useGlotaT = (key, params = {}) => {
  const { translate, currentLocale, isLoading, translationsReady } =
    useGlotaTranslationContext();

  // Inicializa com tradução síncrona local para evitar flash
  const [translation, setTranslation] = useState(() => {
    return glotaTranslationService.translateSync(key, params, currentLocale);
  });

  useEffect(() => {
    // Sempre atualiza quando o locale muda, mesmo durante loading
    const syncTranslation = glotaTranslationService.translateSync(
      key,
      params,
      currentLocale
    );
    if (syncTranslation !== key) {
      setTranslation(syncTranslation);
    }

    // Se ainda está carregando, usa tradução síncrona local
    if (isLoading || !translationsReady) {
      return;
    }

    // Após carregar, busca tradução completa (pode atualizar com GLOTA)
    const loadTranslation = async () => {
      const translated = await translate(key, params);
      setTranslation(translated);
    };

    loadTranslation();
  }, [key, params, translate, currentLocale, isLoading, translationsReady]);

  return translation;
};

/**
 * Hook para tradução síncrona (retorna Promise)
 * Útil para casos onde você precisa aguardar a tradução
 */
export const useGlotaTranslate = () => {
  const { translate } = useGlotaTranslationContext();

  return (key, params = {}) => {
    return translate(key, params);
  };
};

/**
 * Hook para tradução de texto direto
 * Útil para traduzir textos dinâmicos
 */
export const useGlotaTranslateText = () => {
  const { translateText } = useGlotaTranslationContext();

  return (text, sourceLang = 'pt', targetLang = null) => {
    return translateText(text, sourceLang, targetLang);
  };
};

/**
 * Hook para gerenciar o locale atual
 */

const getBackendLocale = (locale) => {
  return LOCALE_MAP[locale] || locale;
};
export const useGlotaLocale = () => {
  const { currentLocale, changeLanguage, isLoading } =
    useGlotaTranslationContext();

  const backendLocale = useMemo(() => {
    return getBackendLocale(currentLocale);
  }, [currentLocale]);

  return {
    currentLocale,
    changeLanguage,
    isLoading,
    backendLocale,
  };
};

export default useGlotaT;
